import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components/macro';
import { compose } from 'ramda';
import { RouteComponentProps } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import { showToast, Typography } from '@deeptrue-frontend/ui';
import api from 'api/api';
import { saveAsFile } from 'utils/exportToCsv';
import { useFormatMessage } from 'components/IntlProvider';
import StoreProvider from 'components/StoreProvider/StoreProvider';
import { OrganizationType } from 'store/modules/auth/auth';
import { WithProjectProps } from 'components/Project/withProject';
import { ReactComponent as DownloadIcon } from 'assets/icons/download.svg';

const { connect, selectors } = StoreProvider;

interface ProjectProps
  extends RouteComponentProps<{ id: string; codename: string }> {
  product: WithProjectProps['product'];
  project: WithProjectProps['project'];
  projectUUID: string;
  organization: OrganizationType;
  isProjectAccepted: WithProjectProps['isProjectAccepted'];
  isProjectArchived: WithProjectProps['isProjectArchived'];
  codename: string;
}

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
    display: 'flex',
    overflow: 'auto',
    flexDirection: 'column',
    border: '1px solid #CECECA',
    boxShadow: ' 0 4px 12px 0 rgba(0,0,0,0.1)',
    borderRadius: '10px',
    cursor: 'pointer',
  },
  file: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    width: '95%',
  },
  aggregate: {
    margin: '0.2rem 0',
  },
  cancel: {
    backgroundColor: '#b00020',
    color: 'white',
    '&:hover': {
      backgroundColor: '#b00020b0',
    },
  },
  animContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: '4rem',
  },
}));

const StyledFile = styled(Paper)`
  flex-direction: row !important;
  svg {
    color: #1f5ee9;
  }
`;

const Header = styled(Typography)`
  font-size: 1.25rem;
  font-weight: 500;
`;

const Files = ({ projectUUID, codename }: ProjectProps) => {
  const classes = useStyles();
  const t = useFormatMessage();
  const [files, setFiles] = useState<{ name: string }[]>([]);
  const [runId, setRunId] = useState<number | null>(null);

  const extractIdFromData = (data: any) =>
    data.results && data.results[0] ? data.results[0].id : null;

  const fetchData = useCallback(() => {
    const orgName = codename;

    projectUUID &&
      api.resources
        .getProjectTableResults(orgName, projectUUID)
        .then(({ data }) => {
          const tableId = extractIdFromData(data);

          tableId &&
            api.resources
              .getProjectTableRuns(orgName, tableId)
              .then(({ data }) => {
                const runId = extractIdFromData(data);

                runId &&
                  api.resources
                    .getProjectRunFiles(orgName, runId)
                    .then(({ data }) => {
                      if (data.files) {
                        setFiles(data.files);
                        setRunId(runId);
                      }
                    });
              });
        });
  }, [codename, projectUUID]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onDownloadFileCsv = (name: string) => {
    api.resources
      .getFileCsv(codename, runId as number, name)
      .then(({ data }) => {
        return saveAsFile(data, name);
      })
      .catch(() => {
        showToast({
          type: 'error',
          title: t(
            'errors.toast.general',
            'Something went wrong, please contact administrator'
          ),
        });
      });
  };

  return (
    <div style={{ marginTop: '2rem', width: '100%' }}>
      <Header>Files to download</Header>
      {files.length > 0 ? (
        <>
          {files.map((file: { name: string }) => (
            <div key={file.name} onClick={() => onDownloadFileCsv(file.name)}>
              <StyledFile className={classes.paper}>
                <span className={classes.file}>{file.name}</span>
                <DownloadIcon />
              </StyledFile>
            </div>
          ))}
        </>
      ) : (
        <Typography>
          {t('noFiles', 'No results. We have nothing to show here')}
        </Typography>
      )}
    </div>
  );
};

export default compose(
  connect((state) => ({
    organization: selectors.authSelectors.userOrganization(state),
    projectUUID: selectors.projects.selected(state).uuid,
  }))
)(Files);
